import { useCallback, useEffect, useState } from 'react';
import { useKeycloak } from '../../authentication/keycloak/KeycloakProvider';
import { getFloorPlanDraft, type FloorPlanDraft } from '../services/floorplan-editor.service';

export interface FloorPlanDraftState {
  /** Undefined while loading or when it could not be read; null when the plan has no draft. */
  readonly draft: FloorPlanDraft | null | undefined;
  /**
   * The draft as last read from the server — what the editor's document was built from. Kept
   * apart from `draft`, which also follows every save: rebuilding the document after each
   * "save draft" would throw away the undo history and the selection for nothing.
   */
  readonly loadedDraft: FloorPlanDraft | null | undefined;
  readonly loading: boolean;
  /** Set when the draft could not be read — the editor then shows the live plan and says so. */
  readonly failed: boolean;
  readonly refresh: () => void;
  /** Takes a draft the server just returned, without reading it again. */
  readonly replace: (draft: FloorPlanDraft | null) => void;
}

/** The plan's unpublished draft, when it has one. */
export const useFloorPlanDraft = (floorPlanId: string | undefined): FloorPlanDraftState => {
  const keycloak = useKeycloak();
  const [draft, setDraft] = useState<FloorPlanDraft | null | undefined>();
  const [loadedDraft, setLoadedDraft] = useState<FloorPlanDraft | null | undefined>();
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);
  const [reloadToken, setReloadToken] = useState(0);

  useEffect(() => {
    if (!floorPlanId) {
      setDraft(null);
      setLoadedDraft(null);
      return undefined;
    }

    let active = true;
    setLoading(true);
    setFailed(false);

    getFloorPlanDraft(floorPlanId, keycloak)
      .then((result) => {
        if (!active) return;
        setDraft(result);
        setLoadedDraft(result);
        setFailed(result === undefined);
      })
      .catch(() => {
        if (active) setFailed(true);
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [floorPlanId, keycloak, reloadToken]);

  const refresh = useCallback(() => setReloadToken((token) => token + 1), []);
  const replace = useCallback((next: FloorPlanDraft | null) => setDraft(next), []);

  return {
    draft, loadedDraft, loading, failed, refresh, replace,
  };
};

export default useFloorPlanDraft;
